import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
  styled,
} from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {
  getBidDetails,
  getBillingMilestones,
  saveBillingMilestones,
} from "../../services/rfqService";
import {
  createContractorProposal,
  getContractorProposal,
  getProposalViewPayments,
  saveContractorBillingMilestones,
  saveProposalPayments,
  submitContractorProposalAction,
  updateContractorProposal,
} from "../../services/contractorService";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import ProposalSummary from "./components/ProposalSummary";
import BillingMilestones from "./components/BillingMilestones";
import Payments from "./components/Payments";
import Contract from "./components/Contract";
import Review from "./components/Review";

const TABS = [
  { id: "summary", label: "Proposal Summary" },
  { id: "milestones", label: "Billing Milestones" },
  { id: "payments", label: "Payments" },
  { id: "contract", label: "Contract" },
  { id: "review", label: "Review" },
];

const StyledTabs = styled(Tabs)({
  display: "flex",
  padding: 0,
  minHeight: "36px",
  "& .MuiTabs-flexContainer": {
    gap: "8px",
  },
});

const StyledTab = styled(Tab)(({ selected }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "6px 14px",
  minHeight: "36px",
  height: "36px",
  fontSize: "13px",
  borderRadius: "999px",
  fontFamily: "Inter Tight",
  fontWeight: 600,
  textTransform: "none",
  backgroundColor: selected ? "#0D0D0D" : "#F7F7F7",
  color: selected ? "#FFFFFF !important" : "#949DAA !important",
  transition: "all 0.2s ease-in-out",
  marginRight: 0,
  "&:hover": {
    backgroundColor: selected ? "#0D0D0D" : "#EDEDED",
    color: selected ? "#FFFFFF" : "#000",
  },
  "&.Mui-disabled": {
    opacity: 0.8,
    cursor: "not-allowed",
    pointerEvents: "auto",
    backgroundColor: "#F7F7F7",
    color: "#949DAA !important",
  },
}));

const TabNumber = styled("span")(({ selected }) => ({
  width: "22px",
  height: "22px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "999px",
  fontSize: "11px",
  fontWeight: 600,
  padding: 0,
  backgroundColor: selected ? "#3d3d3d" : "#FFFFFF",
  color: selected ? "#FFFFFF" : "#949DAA",
  border: "none !important",
  flexShrink: 0,
}));

const PrimaryButton = styled(Button)(() => ({
  backgroundColor: "#4D6BF6",
  color: "#fff",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "10px",
  boxShadow: "none",
  padding: "8px 18px",
  "&:hover": { backgroundColor: "#3B5BDB", boxShadow: "none" },
  // MUI's default disabled style washes the label out to grey — keep
  // the "Saving..." text white so the button still reads as "active /
  // working" rather than "blocked".
  "&.Mui-disabled": {
    backgroundColor: "#4D6BF6",
    color: "#fff",
    opacity: 0.7,
  },
}));

const SecondaryButton = styled(Button)(() => ({
  backgroundColor: "#fff",
  color: "#0D0D0E",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "10px",
  border: "1px solid #D7D8D9",
  boxShadow: "none",
  padding: "8px 18px",
  "&:hover": { backgroundColor: "#F9FAFB", boxShadow: "none" },
}));

let _milestoneIdSeq = 0;
const nextMilestoneId = () =>
  `m_${Date.now().toString(36)}_${++_milestoneIdSeq}`;

const isoToDateInput = (val) => {
  if (!val) return "";
  if (typeof val !== "string") return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  if (val.includes("T")) return val.split("T")[0];
  return val;
};

const parseProposalResponse = (data) => {
  if (!data) return null;

  const proposalVal = data.value ?? data.proposal_value ?? null;
  const loc = data.location_address || data.location || "";

  let timelineParsed = data.timeline;
  if (typeof timelineParsed === "string") {
    try {
      timelineParsed = JSON.parse(timelineParsed);
    } catch {
      timelineParsed = null;
    }
  }
  if (!timelineParsed || typeof timelineParsed !== "object") {
    timelineParsed = {
      Approvals: "",
      "Demolition & Site Prep": "",
      Construction: "",
      "Finishing & Handover": "",
    };
  }

  const customerDetails = {
    username: data.customer_name || "",
    customer_email: data.customer_email || "",
  };
  const selectedCustomer =
    data.customer_name || data.customer_email
      ? {
          id: data.client_id || 0,
          // Keep the proposal's saved client_id on the object so we send
          // the same value back on update (instead of resetting to 0).
          client_id: data.client_id == null ? 0 : data.client_id,
          username: data.customer_name || "",
          customer_email: data.customer_email || "",
        }
      : null;

  const apiMilestones = Array.isArray(data.billing_milestones)
    ? data.billing_milestones
    : [];
  const sorted = [...apiMilestones].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );
  const billingMilestones = sorted.map((m) => {
    const phase = (m.description || m.name || m.groupName || "")
      .trim()
      .toLowerCase();
    const type = (m.type || "").toUpperCase();
    const isInitialDeposit =
      !!m.isInitialDeposit ||
      type === "PROJECT_INITIAL_DEPOSIT" ||
      phase === "initial deposit";
    const isMandatory =
      !!m.isMandatory ||
      isInitialDeposit ||
      type === "HANDOVER" ||
      type === "PROJECT_COMPLETION" ||
      phase === "handover";
    let parsedDeliverables = m.deliverables;
    if (typeof parsedDeliverables === "string") {
      try {
        parsedDeliverables = JSON.parse(parsedDeliverables);
      } catch {
        parsedDeliverables = [];
      }
    }
    if (!Array.isArray(parsedDeliverables)) parsedDeliverables = [];
    return {
      id: m.id ?? nextMilestoneId(),
      description: m.description || m.name || m.groupName || "",
      percentage: m.percentage ?? "",
      amount: m.amount ?? "",
      due_date: isoToDateInput(m.due_date || m.dueDate),
      isInitialDeposit,
      isMandatory,
      type: m.type,
      deliverables: parsedDeliverables.map((d) => ({
        id: d.id || nextMilestoneId(),
        deliverable_description: d.deliverable_description || d.name || "",
        sub_activities: (d.sub_activities || []).map((s) => ({
          id: s.id || nextMilestoneId(),
          sub_activity_description:
            s.sub_activity_description || s.name || "",
        })),
      })),
    };
  });

  return {
    projectName: data.project_name || "",
    projectImage: data.image_url || null,
    projectLocation: loc,
    proposalDescription: data.description || data.scope || "",
    proposalValue:
      proposalVal !== undefined && proposalVal !== null
        ? String(proposalVal)
        : "",
    timeline: timelineParsed,
    customerDetails,
    selectedCustomer,
    billingMilestones,
    hasMilestones: apiMilestones.length > 0,
    proposalStatus: (data.proposal_status || data.status || "").toLowerCase(),
    raw: data,
  };
};

const DEFAULT_MILESTONES = [
  {
    id: nextMilestoneId(),
    description: "Initial Deposit",
    percentage: "0",
    amount: "",
    deliverables: [],
    isInitialDeposit: true,
    isMandatory: true,
    due_date: "",
  },
  {
    id: nextMilestoneId(),
    description: "Handover",
    percentage: "20",
    amount: "",
    deliverables: [],
    isMandatory: true,
    due_date: "",
  },
];

const RFCPricing = () => {
  const { id, bidId, proposalId: proposalIdParam } = useParams();
  const navigate = useNavigate();

  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submittingTab1, setSubmittingTab1] = useState(false);
  const [submittingTab2, setSubmittingTab2] = useState(false);
  const [submittingPayments, setSubmittingPayments] = useState(false);
  const [proposalId, setProposalId] = useState(proposalIdParam || null);

  const [projectName, setProjectName] = useState("");
  const [projectImage, setProjectImage] = useState(null);
  const [projectLocation, setProjectLocation] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [contractFile, setContractFile] = useState(null);
  const [renoContractFile, setRenoContractFile] = useState(null);

  const [proposalDescription, setProposalDescription] = useState("");
  const [proposalValue, setProposalValue] = useState("");
  const [customerDetails, setCustomerDetails] = useState({
    username: "",
    customer_email: "",
  });
  const [errObj, setErrObj] = useState({
    projectNameErr: false,
    projectNameMsg: "",
    descriptionErr: false,
    descriptionMsg: "",
    proposalValueErr: false,
    proposalValueMsg: "",
    customerNameErr: false,
    customerNameMsg: "",
    customerEmailErr: false,
    customerEmailMsg: "",
    timelineErr: false,
    timelineMsg: "",
  });
  const [timeline, setTimeline] = useState({
    Approvals: "",
    "Demolition & Site Prep": "",
    Construction: "",
    "Finishing & Handover": "",
  });

  const [billingMilestones, setBillingMilestonesState] = useState(
    DEFAULT_MILESTONES.map((m) => ({
      ...m,
      deliverables: [...(m.deliverables || [])],
    }))
  );
  const [billingMilestonesData, setBillingMilestonesData] = useState(null);
  const [hasMilestoneChanges, setHasMilestoneChanges] = useState(false);
  const [hasExistingMilestones, setHasExistingMilestones] = useState(false);
  // True once the proposal has saved payments on the backend — sourced
  // from the Payments tab's /reno-fees fetch (setup branch) or from
  // a successful click of the bottom Save button. Gates Submit Proposal.
  const [paymentsSaved, setPaymentsSaved] = useState(false);
  const [proposalStatus, setProposalStatus] = useState("");
  const [serverProposal, setServerProposal] = useState(null);
  // Drives the "Are you sure you want to submit?" confirmation modal —
  // opens on Submit click *after* validations pass, and the actual
  // /action POST runs only when the user confirms inside the dialog.
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const formInitializedRef = useRef(false);
  const paymentsRef = useRef(null);

  // True when the proposal is no longer editable (any status other than
  // draft — submitted, won, lost, etc.). All inputs become disabled, the
  // bottom Next button skips API saves, and Submit Proposal is replaced
  // by Close on the last tab. The fieldset wrapper around tab content
  // does the native disable; this flag also gates handler behavior.
  const isReadOnly = !!proposalStatus && proposalStatus !== "draft";

  // ---------- Keep proposalId state in sync with route param ----------
  useEffect(() => {
    if (proposalIdParam) setProposalId(proposalIdParam);
  }, [proposalIdParam]);

  // ---------- Refetch proposal details (manual + on tab switch) ----------
  const refetchProposal = useCallback(async () => {
    if (!proposalId) return null;
    try {
      const res = await getContractorProposal(proposalId);
      const data = res?.data || res;
      const parsed = parseProposalResponse(data);
      if (parsed) {
        setServerProposal(parsed);
        setProposalStatus(parsed.proposalStatus || "");
      }
      return parsed;
    } catch (e) {
      console.error("Failed to refetch proposal:", e);
      return null;
    }
  }, [proposalId]);

  // ---------- Check saved-payments status on the server independently of
  //           the Payments tab mounting. Without this, jumping straight to
  //           the Review tab leaves `paymentsSaved` false and Submit
  //           Proposal bounces back to tab 2 even though payments exist. ----------
  useEffect(() => {
    if (!proposalId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getProposalViewPayments(proposalId);
        if (cancelled) return;
        const data = res?.data || res || null;
        const isSetup =
          !!data &&
          data.billingId !== undefined &&
          data.billingId !== null &&
          data.billingId !== "";
        setPaymentsSaved(isSetup);
      } catch (e) {
        console.error("Failed to check view-payments:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [proposalId]);

  // ---------- Load proposal details: populate form on first load,
  //           refetch on every tab switch to keep server data fresh ----------
  useEffect(() => {
    if (!proposalId) return;
    let cancelled = false;
    const isInitialLoad = !formInitializedRef.current;
    (async () => {
      if (isInitialLoad) setLoading(true);
      try {
        const res = await getContractorProposal(proposalId).catch(() => null);
        if (cancelled || !res) return;
        const data = res?.data || res;
        const parsed = parseProposalResponse(data);
        if (!parsed) return;

        setServerProposal(parsed);
        setProposalStatus(parsed.proposalStatus || "");

        if (isInitialLoad) {
          if (parsed.projectName) setProjectName(parsed.projectName);
          if (parsed.projectImage) setProjectImage(parsed.projectImage);
          if (parsed.projectLocation) setProjectLocation(parsed.projectLocation);
          if (parsed.proposalDescription)
            setProposalDescription(parsed.proposalDescription);
          if (parsed.proposalValue) setProposalValue(parsed.proposalValue);
          setTimeline(parsed.timeline);
          if (
            parsed.customerDetails.username ||
            parsed.customerDetails.customer_email
          ) {
            setCustomerDetails(parsed.customerDetails);
            if (parsed.selectedCustomer)
              setSelectedCustomer(parsed.selectedCustomer);
          }
          if (parsed.hasMilestones) {
            setHasExistingMilestones(true);
            setBillingMilestonesData(parsed.raw.billing_milestones);
            setBillingMilestonesState(parsed.billingMilestones);
          } else {
            setHasExistingMilestones(false);
          }
          formInitializedRef.current = true;
        }
      } finally {
        if (!cancelled && isInitialLoad) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [proposalId, tabIndex]);

  // ---------- Load existing data when id+bidId are present ----------
  useEffect(() => {
    if (!id || !bidId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [bidRes, msRes] = await Promise.all([
          getBidDetails(id, bidId).catch(() => null),
          getBillingMilestones(bidId).catch(() => null),
        ]);
        if (cancelled) return;
        const bidData = bidRes?.data?.[0] || bidRes?.data;
        if (bidData) {
          setProposalDescription(
            bidData.scope_of_work || bidData.description || ""
          );
          setProjectName(bidData.project_name || bidData.name || "");
          setProjectLocation(bidData.location || "");
          setCustomerDetails({
            username: bidData.username || "",
            customer_email: bidData.customer_email || "",
          });
          if (bidData.username) {
            const existing = {
              id: bidData.client_id || bidData.user_id || 0,
              username: bidData.username,
              customer_email: bidData.customer_email || "",
            };
            setSelectedCustomer(existing);
          }
          if (bidData.timeline) setTimeline(bidData.timeline);
        }
        if (msRes?.success) {
          const ms = Array.isArray(msRes.data?.milestones)
            ? msRes.data.milestones
            : Array.isArray(msRes.data)
            ? msRes.data
            : Array.isArray(msRes.billing_milestones)
            ? msRes.billing_milestones
            : [];
          if (ms.length > 0) {
            setBillingMilestonesState(
              ms.map((m) => {
                const phase = (m.description || m.name || "")
                  .trim()
                  .toLowerCase();
                const type = (m.type || "").toUpperCase();
                const isInitialDeposit =
                  !!m.isInitialDeposit ||
                  type === "PROJECT_INITIAL_DEPOSIT" ||
                  phase === "initial deposit";
                const isMandatory =
                  !!m.isMandatory ||
                  isInitialDeposit ||
                  type === "HANDOVER" ||
                  phase === "handover";
                return {
                  id: m.id || nextMilestoneId(),
                  description: m.description || m.name || "",
                  percentage: m.percentage ?? "",
                  amount: m.amount ?? "",
                  due_date: m.due_date || "",
                  isInitialDeposit,
                  isMandatory,
                  type: m.type,
                  deliverables: (m.deliverables || m.activities || []).map(
                    (d) => ({
                      id: d.id || nextMilestoneId(),
                      deliverable_description:
                        d.deliverable_description || d.name || "",
                      sub_activities: (d.sub_activities || []).map((s) => ({
                        id: s.id || nextMilestoneId(),
                        sub_activity_description:
                          s.sub_activity_description || s.name || "",
                      })),
                    })
                  ),
                };
              })
            );
          }
          setBillingMilestonesData(msRes.data || msRes || null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, bidId]);

  // ---------- Milestone helpers ----------

  const totalPercentage = useMemo(
    () =>
      billingMilestones.reduce(
        (acc, m) => acc + (Number(m.percentage) || 0),
        0
      ),
    [billingMilestones]
  );

  const setMilestones = (next) => {
    setBillingMilestonesState(next);
    setHasMilestoneChanges(true);
  };

  useEffect(() => {
    const pv = Number(proposalValue) || 0;
    if (pv <= 0) return;
    setBillingMilestonesState((prev) => {
      let changed = false;
      const next = prev.map((m) => {
        const pct = Number(m.percentage) || 0;
        const expected = Math.round((pct / 100) * pv);
        if ((Number(m.amount) || 0) === expected) return m;
        changed = true;
        return { ...m, amount: pct > 0 ? expected : "" };
      });
      return changed ? next : prev;
    });
  }, [proposalValue]);

  const addMilestone = () =>
    setMilestones([
      ...billingMilestones,
      {
        id: nextMilestoneId(),
        description: "",
        percentage: "",
        amount: "",
        due_date: "",
        deliverables: [],
      },
    ]);

  const updateMilestone = (index, field, value) => {
    const next = billingMilestones.map((m, i) => {
      if (i !== index) return m;
      const updated = { ...m, [field]: value };
      if (field === "percentage") {
        const pct = Number(value) || 0;
        const pv = Number(proposalValue) || 0;
        updated.amount = pv > 0 ? Math.round((pct / 100) * pv) : "";
      }
      return updated;
    });
    setMilestones(next);
  };

  const deleteMilestone = (index) => {
    const next = billingMilestones.filter((_, i) => i !== index);
    setMilestones(next);
  };

  const reorderMilestones = (fromIndex, toIndex) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= billingMilestones.length ||
      toIndex >= billingMilestones.length
    ) {
      return;
    }
    const initialDepositIdx = billingMilestones.findIndex(isInitialDepositMs);
    if (initialDepositIdx !== -1) {
      if (fromIndex === initialDepositIdx) return;
      if (toIndex <= initialDepositIdx) return;
    }
    const next = [...billingMilestones];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setMilestones(next);
  };

  // Deliverables CRUD
  const addDeliverable = (msIndex) => {
    const next = billingMilestones.map((m, i) =>
      i === msIndex
        ? {
            ...m,
            deliverables: [
              ...(m.deliverables || []),
              {
                id: nextMilestoneId(),
                deliverable_description: "",
                sub_activities: [],
              },
            ],
          }
        : m
    );
    setMilestones(next);
  };

  const updateDeliverable = (msIndex, dIndex, value) => {
    const next = billingMilestones.map((m, i) => {
      if (i !== msIndex) return m;
      const dels = (m.deliverables || []).map((d, j) =>
        j === dIndex ? { ...d, ...value } : d
      );
      return { ...m, deliverables: dels };
    });
    setMilestones(next);
  };

  const deleteDeliverable = (msIndex, dIndex) => {
    const next = billingMilestones.map((m, i) => {
      if (i !== msIndex) return m;
      const dels = (m.deliverables || []).filter((_, j) => j !== dIndex);
      return { ...m, deliverables: dels };
    });
    setMilestones(next);
  };

  const addSubActivity = (msIndex, dIndex) => {
    const next = billingMilestones.map((m, i) => {
      if (i !== msIndex) return m;
      const dels = (m.deliverables || []).map((d, j) =>
        j === dIndex
          ? {
              ...d,
              sub_activities: [
                ...(d.sub_activities || []),
                {
                  id: nextMilestoneId(),
                  sub_activity_description: "",
                },
              ],
            }
          : d
      );
      return { ...m, deliverables: dels };
    });
    setMilestones(next);
  };

  const updateSubActivity = (msIndex, dIndex, sIndex, sub) => {
    const next = billingMilestones.map((m, i) => {
      if (i !== msIndex) return m;
      const dels = (m.deliverables || []).map((d, j) => {
        if (j !== dIndex) return d;
        const subs = (d.sub_activities || []).map((s, k) =>
          k === sIndex ? { ...s, ...sub } : s
        );
        return { ...d, sub_activities: subs };
      });
      return { ...m, deliverables: dels };
    });
    setMilestones(next);
  };

  const deleteSubActivity = (msIndex, dIndex, sIndex) => {
    const next = billingMilestones.map((m, i) => {
      if (i !== msIndex) return m;
      const dels = (m.deliverables || []).map((d, j) => {
        if (j !== dIndex) return d;
        const subs = (d.sub_activities || []).filter((_, k) => k !== sIndex);
        return { ...d, sub_activities: subs };
      });
      return { ...m, deliverables: dels };
    });
    setMilestones(next);
  };

  const buildMilestonesPayload = () =>
    billingMilestones.map((m, idx) => ({
      description: m.description || "",
      due_date: m.due_date || null,
      percentage: Number(m.percentage) || 0,
      amount: Number(m.amount) || 0,
      sort_order: idx,
      deliverables: (m.deliverables || []).map((d) => ({
        id: d.id,
        deliverable_description: d.deliverable_description || "",
        sub_activities: (d.sub_activities || []).map((s) => ({
          id: s.id,
          sub_activity_description: s.sub_activity_description || "",
        })),
      })),
    }));

  // ---------- Save ----------

  const validateCustomer = () => {
    const errors = { ...errObj };
    let valid = true;
    const customerName =
      selectedCustomer?.username || customerDetails.username || "";
    if (!customerName.trim()) {
      errors.customerNameErr = true;
      errors.customerNameMsg = "Please select or add a customer";
      valid = false;
    } else {
      errors.customerNameErr = false;
      errors.customerNameMsg = "";
    }
    const customerEmail =
      selectedCustomer?.customer_email || customerDetails.customer_email || "";
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (customerEmail && !emailRegex.test(customerEmail.trim())) {
      errors.customerEmailErr = true;
      errors.customerEmailMsg = "Please provide a valid email";
      valid = false;
    } else {
      errors.customerEmailErr = false;
      errors.customerEmailMsg = "";
    }
    setErrObj(errors);
    return valid;
  };

  const validateTab1 = () => {
    const errors = { ...errObj };
    let valid = true;
    if (!projectName?.trim()) {
      errors.projectNameErr = true;
      errors.projectNameMsg = "Please enter a project name";
      valid = false;
    } else {
      errors.projectNameErr = false;
      errors.projectNameMsg = "";
    }
    if (!proposalDescription?.trim()) {
      errors.descriptionErr = true;
      errors.descriptionMsg = "Please enter a description";
      valid = false;
    } else {
      errors.descriptionErr = false;
      errors.descriptionMsg = "";
    }
    const pvStr = String(proposalValue ?? "").trim();
    if (!pvStr) {
      errors.proposalValueErr = true;
      errors.proposalValueMsg = "Please enter the proposal value";
      valid = false;
    } else if (!/^\d+$/.test(pvStr) || Number(pvStr) <= 0) {
      errors.proposalValueErr = true;
      errors.proposalValueMsg = "Enter a positive whole number";
      valid = false;
    } else {
      errors.proposalValueErr = false;
      errors.proposalValueMsg = "";
    }
    const timelineKeys = [
      "Approvals",
      "Demolition & Site Prep",
      "Construction",
      "Finishing & Handover",
    ];
    const timelineMissing = timelineKeys.some(
      (k) => !timeline?.[k] || String(timeline[k]).trim() === ""
    );
    if (timelineMissing) {
      errors.timelineErr = true;
      errors.timelineMsg = "Please fill in all timeline fields";
      valid = false;
    } else {
      errors.timelineErr = false;
      errors.timelineMsg = "";
    }
    setErrObj(errors);
    return validateCustomer() && valid;
  };

  const handleAddLocalCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerDetails({
      username: customer?.username || "",
      customer_email: customer?.customer_email || "",
    });
    setErrObj((prev) => ({
      ...prev,
      customerNameErr: false,
      customerNameMsg: "",
      customerEmailErr: false,
      customerEmailMsg: "",
    }));
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerDetails({
      username: customer?.username || "",
      customer_email: customer?.customer_email || "",
    });
  };

  const buildProposalPayload = () => {
    let imageUrl = "";
    if (projectImage) {
      imageUrl =
        typeof projectImage === "string"
          ? projectImage
          : URL.createObjectURL(projectImage);
    }
    const customerName =
      selectedCustomer?.username || customerDetails.username || "";
    const customerEmail =
      selectedCustomer?.customer_email || customerDetails.customer_email || "";
    // `client_id` comes from the customer record:
    //   - Created via "Add ..." → POST /link-customer's client_id (null → 0)
    //   - Selected from search  → the option's user_id          (null → 0)
    // Both are normalized onto `selectedCustomer.client_id` upstream, so we
    // just read it here.
    const rawClientId = selectedCustomer?.client_id;
    const clientId =
      rawClientId == null || rawClientId === ""
        ? 0
        : Number(rawClientId) || 0;
    return {
      description: proposalDescription || "",
      timeline: JSON.stringify(timeline || {}),
      client_id: clientId,
      project_name: projectName || "",
      image_url: imageUrl,
      location_address: projectLocation || "",
      customer_name: customerName,
      customer_email: customerEmail,
      value: Number(proposalValue) || 0,
    };
  };

  const isInitialDepositMs = (m) => {
    if (!m) return false;
    if (m.isInitialDeposit) return true;
    if ((m.type || "").toUpperCase() === "PROJECT_INITIAL_DEPOSIT") return true;
    return (m.description || m.name || "").trim().toLowerCase() === "initial deposit";
  };

  const validateMilestones = () => {
    if (!billingMilestones || billingMilestones.length === 0) {
      toast.error("Please add at least one milestone");
      return false;
    }
    for (let i = 0; i < billingMilestones.length; i += 1) {
      const m = billingMilestones[i];
      const label = m.description || m.name || `Milestone ${i + 1}`;
      const pct = Number(m.percentage);
      const pctEmpty =
        m.percentage === "" ||
        m.percentage === undefined ||
        m.percentage === null;
      if (pctEmpty || Number.isNaN(pct) || pct < 0) {
        toast.error(`Please enter a percentage for "${label}"`);
        return false;
      }
      if (!isInitialDepositMs(m) && pct <= 0) {
        toast.error(`Please enter a percentage for "${label}"`);
        return false;
      }
      const amt = Number(m.amount);
      const amtEmpty =
        m.amount === "" || m.amount === undefined || m.amount === null;
      if (amtEmpty || Number.isNaN(amt) || amt < 0) {
        toast.error(`Please enter an amount for "${label}"`);
        return false;
      }
      if (!isInitialDepositMs(m) && amt <= 0) {
        toast.error(`Please enter an amount for "${label}"`);
        return false;
      }
      if (isInitialDepositMs(m) && !m.due_date) {
        toast.error("Please select a due date for the Initial Deposit");
        return false;
      }
    }
    if (Math.abs(totalPercentage - 100) > 0.01) {
      toast.error(
        `Milestone percentages must total 100% (currently ${totalPercentage.toFixed(
          2
        )}%)`
      );
      return false;
    }
    return true;
  };

  const milestoneTypeFor = (m) => {
    if (isInitialDepositMs(m)) return "PROJECT_INITIAL_DEPOSIT";
    const phase = (m.description || m.name || "").trim().toLowerCase();
    if (phase === "handover") return "PROJECT_COMPLETION";
    return "PAYMENT_GROUP";
  };

  const toIsoDate = (d) => {
    if (!d) return null;
    if (typeof d === "string" && d.includes("T")) return d;
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? null : dt.toISOString();
  };

  const buildContractorMilestonesPayload = () =>
    billingMilestones.map((m, idx) => ({
      description: m.description || m.name || "",
      due_date: toIsoDate(m.due_date),
      percentage: Number(m.percentage) || 0,
      amount: Number(m.amount) || 0,
      sort_order: idx,
      type: milestoneTypeFor(m),
      deliverables: JSON.stringify(
        (m.deliverables || []).map((d) => ({
          deliverable_description: d.deliverable_description || d.name || "",
          sub_activities: (d.sub_activities || []).map((s) => ({
            sub_activity_description: s.sub_activity_description || s.name || "",
          })),
        }))
      ),
    }));

  const submitPaymentsAndAdvance = async () => {
    if (!proposalId) {
      toast.error("Save the proposal first");
      setTabIndex(0);
      return;
    }
    if (!paymentsRef.current) return;
    if (!paymentsRef.current.validate()) return;
    const payload = paymentsRef.current.buildSavePayload();
    console.log("Save payments payload:", payload);
    setSubmittingPayments(true);
    try {
      const res = await saveProposalPayments(proposalId, payload);
      console.log("Save payments response:", res);
      setPaymentsSaved(true);
      setTabIndex(3);
    } catch (e) {
      console.log("Save payments error:", e);
      toast.error(e?.message || "Failed to save payments");
    } finally {
      setSubmittingPayments(false);
    }
  };

  const submitTab2AndAdvance = async () => {
    if (!proposalId) {
      toast.error("Save the proposal first");
      setTabIndex(0);
      return;
    }
    if (!validateMilestones()) return;
    const payload = buildContractorMilestonesPayload();
    const method = hasExistingMilestones ? "PUT" : "POST";
    console.log("Billing milestones payload:", payload, "method:", method);
    setSubmittingTab2(true);
    try {
      const res = await saveContractorBillingMilestones(
        proposalId,
        payload,
        method
      );
      console.log("Billing milestones response:", res);
      setHasMilestoneChanges(false);
      setHasExistingMilestones(true);
      setTabIndex(2);
    } catch (e) {
      console.log("Billing milestones error:", e);
      toast.error(e?.message || "Failed to save milestones");
    } finally {
      setSubmittingTab2(false);
    }
  };

  const submitTab1AndAdvance = async () => {
    if (!validateTab1()) {
      toast.error("Please fill in the required fields");
      return;
    }
    const payload = buildProposalPayload();
    const isUpdate = !!proposalIdParam;
    console.log(
      "Contractor proposal payload:",
      payload,
      "method:",
      isUpdate ? "PUT" : "POST"
    );
    setSubmittingTab1(true);
    try {
      const res = isUpdate
        ? await updateContractorProposal(proposalIdParam, payload)
        : await createContractorProposal(payload);
      console.log("Contractor proposal response:", res);
      const data = res?.data || res || {};
      const newProposalId = data.proposal_id || data.id;
      if (!isUpdate && newProposalId) {
        setProposalId(newProposalId);
        navigate(`/contractor/proposal/${newProposalId}`, { replace: true });
      }
      setTabIndex(1);
    } catch (e) {
      console.log("Contractor proposal error:", e);
      toast.error(
        e?.message || (isUpdate ? "Failed to update proposal" : "Failed to create proposal")
      );
    } finally {
      setSubmittingTab1(false);
    }
  };

  const saveMilestoneChanges = async () => {
    if (!bidId) {
      toast.info("Save the proposal first to persist milestones.");
      return;
    }
    setSaving(true);
    try {
      const proposalIds = bidId ? [bidId] : [];
      const payload = buildMilestonesPayload();
      console.log("Billing milestones payload:", payload);
      const res = await saveBillingMilestones(proposalIds, payload);
      if (res?.success) {
        toast.success("Billing milestones saved");
        setHasMilestoneChanges(false);
      } else {
        toast.error(res?.message || "Failed to save milestones");
      }
    } catch {
      toast.error("Failed to save milestones");
    } finally {
      setSaving(false);
    }
  };

  // Pre-submit gate. Validations stay inline (toast + tab jump) — only
  // when everything passes do we open the confirmation modal. The actual
  // POST happens in `confirmSubmitProposal` after the user clicks Yes.
  const handleSubmitProposal = () => {
    if (!proposalId) {
      toast.error("Please complete the proposal summary before submitting");
      setTabIndex(0);
      return;
    }
    if (!validateCustomer()) {
      setTabIndex(0);
      return;
    }
    if (!hasExistingMilestones) {
      toast.error("Please add billing milestones before submitting");
      setTabIndex(1);
      return;
    }
    if (!paymentsSaved) {
      toast.error("Please configure and save payments before submitting");
      setTabIndex(2);
      return;
    }
    setSubmitConfirmOpen(true);
  };

  const closeSubmitConfirm = () => {
    if (saving) return;
    setSubmitConfirmOpen(false);
  };

  const confirmSubmitProposal = async () => {
    if (!proposalId) return;
    setSaving(true);
    try {
      const res = await submitContractorProposalAction(proposalId, "SUBMIT");
      console.log("Proposal submit response:", res);
      setProposalStatus("submitted");
      toast.success("Proposal submitted");
      setSubmitConfirmOpen(false);
      navigate("/contractor/proposals");
    } catch (e) {
      console.log("Proposal submit error:", e);
      toast.error(e?.message || "Failed to submit proposal");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Render ----------

  if (loading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "center",
          padding: { xs: "2vh 4vw 1vh", md: "3vh 2.5vw 1.5vh" },
          gap: "10px",
          background:
            "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
          flexShrink: 0,
        }}
      >
        <LeftArrowIcon
          onClick={() => navigate("/contractor/proposals")}
          style={{ cursor: "pointer" }}
        />
      </Box>

      <Box
        sx={{
          padding: { xs: "10px 4vw 12px", md: "12px 2vw 16px" },
          flexShrink: 0,
        }}
      >
        <StyledTabs
          value={tabIndex}
          variant="scrollable"
          scrollButtons={false}
          onChange={(_, v) => setTabIndex(v)}
          aria-label="RFC Tabs"
          sx={{ "& .MuiTabs-indicator": { background: "none" } }}
        >
          {TABS.map((t, index) => (
            <StyledTab
              key={t.id}
              disabled={index > 0 && !proposalId}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <TabNumber selected={tabIndex === index}>
                    {`0${index + 1}`}
                  </TabNumber>
                  <span>{t.label}</span>
                </Box>
              }
            />
          ))}
        </StyledTabs>
      </Box>

      <Box
        component="fieldset"
        disabled={isReadOnly}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: { xs: "1vh 4vw 2vh", md: "1vh 2vw 2vh 2vw" },
          // strip native fieldset chrome so layout matches the editable mode
          border: "none",
          margin: 0,
          minWidth: 0,
        }}
      >
        {tabIndex === 0 && (
          <ProposalSummary
            projectName={projectName}
            setProjectName={setProjectName}
            projectImage={projectImage}
            setProjectImage={setProjectImage}
            projectLocation={projectLocation}
            setProjectLocation={setProjectLocation}
            proposalDescription={proposalDescription}
            setProposalDescription={setProposalDescription}
            proposalValue={proposalValue}
            setProposalValue={setProposalValue}
            selectedCustomer={selectedCustomer}
            onSelectCustomer={handleSelectCustomer}
            onAddCustomer={handleAddLocalCustomer}
            errObj={errObj}
            setErrObj={setErrObj}
            timeline={timeline}
            setTimeline={setTimeline}
            isDisabled={isReadOnly}
          />
        )}
        {tabIndex === 1 && (
          <BillingMilestones
            billingMilestonesData={billingMilestonesData}
            billingMilestones={billingMilestones}
            hasMilestoneChanges={hasMilestoneChanges}
            isMilestoneLoading={saving}
            addMilestone={addMilestone}
            deleteMilestone={deleteMilestone}
            updateMilestone={updateMilestone}
            saveMilestoneChanges={saveMilestoneChanges}
            reorderMilestones={reorderMilestones}
            addDeliverable={addDeliverable}
            updateDeliverable={updateDeliverable}
            deleteDeliverable={deleteDeliverable}
            addSubActivity={addSubActivity}
            updateSubActivity={updateSubActivity}
            deleteSubActivity={deleteSubActivity}
            totalPercentage={totalPercentage}
            proposalValue={proposalValue}
            hideSaveButton={!bidId}
            isDisabled={isReadOnly}
          />
        )}
        {tabIndex === 2 && (
          <Payments
            ref={paymentsRef}
            proposalId={proposalId}
            projectId={
              serverProposal?.raw?.project_id ||
              serverProposal?.raw?.projectId ||
              null
            }
            proposalValue={
              serverProposal?.proposalValue || proposalValue || ""
            }
            billingMilestones={billingMilestonesData}
            onEditMilestones={() => setTabIndex(1)}
            onPaymentsSavedChange={setPaymentsSaved}
            isReadOnly={isReadOnly}
          />
        )}
        {tabIndex === 3 && (
          <Contract
            contract={contractFile}
            onChange={setContractFile}
            renoContract={renoContractFile}
            onChangeRenoContract={setRenoContractFile}
            isDisabled={isReadOnly}
          />
        )}
        {tabIndex === 4 && (
          <Review
            proposalId={proposalId}
            projectName={serverProposal?.projectName || ""}
            projectImage={serverProposal?.projectImage || null}
            projectLocation={serverProposal?.projectLocation || ""}
            proposalDescription={serverProposal?.proposalDescription || ""}
            proposalValue={serverProposal?.proposalValue || ""}
            customerDetails={
              serverProposal?.customerDetails || {
                username: "",
                customer_email: "",
              }
            }
            selectedCustomer={serverProposal?.selectedCustomer || null}
            billingMilestones={serverProposal?.billingMilestones || []}
            timeline={
              serverProposal?.timeline || {
                Approvals: "",
                "Demolition & Site Prep": "",
                Construction: "",
                "Finishing & Handover": "",
              }
            }
            contract={contractFile}
            renoContract={renoContractFile}
          />
        )}
      </Box>

      <Box
        sx={{
          padding: { xs: "12px 4vw", md: "1.5vh 2vw" },
          backgroundColor: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          borderTop: { xs: "1px solid #E5E7EB", md: "none" },
        }}
      >
        <SecondaryButton onClick={() => navigate("/contractor/proposals")}>
          Cancel
        </SecondaryButton>
        {tabIndex < TABS.length - 1 ? (
          <PrimaryButton
            onClick={() => {
              // Read-only mode: skip every save endpoint, just advance.
              if (isReadOnly) {
                setTabIndex(tabIndex + 1);
                return;
              }
              if (tabIndex === 0) {
                submitTab1AndAdvance();
              } else if (tabIndex === 1) {
                submitTab2AndAdvance();
              } else if (tabIndex === 2) {
                submitPaymentsAndAdvance();
              } else {
                setTabIndex(tabIndex + 1);
              }
            }}
            disabled={
              !isReadOnly &&
              ((tabIndex === 0 && submittingTab1) ||
                (tabIndex === 1 && submittingTab2) ||
                (tabIndex === 2 && submittingPayments))
            }
          >
            {isReadOnly
              ? "Next"
              : tabIndex === 0 && submittingTab1
              ? "Saving..."
              : tabIndex === 1 && submittingTab2
              ? "Saving..."
              : tabIndex === 2 && submittingPayments
              ? "Saving..."
              : tabIndex === 2
              ? "Save"
              : "Next"}
          </PrimaryButton>
        ) : proposalStatus && proposalStatus !== "draft" ? (
          <PrimaryButton onClick={() => navigate("/contractor/proposals")}>
            Close
          </PrimaryButton>
        ) : (
          <PrimaryButton onClick={handleSubmitProposal} disabled={saving}>
            {saving ? "Submitting..." : "Submit Proposal"}
          </PrimaryButton>
        )}
      </Box>

      <Dialog
        open={submitConfirmOpen}
        onClose={closeSubmitConfirm}
        PaperProps={{
          sx: { borderRadius: "12px", width: "min(440px, 92vw)" },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Inter Tight",
            fontSize: 18,
            fontWeight: 600,
            color: "#0D0D0E",
            pb: 0.5,
          }}
        >
          Submit Proposal?
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography
            sx={{
              fontSize: 14,
              color: "#4B5563",
              fontFamily: "Inter Tight",
              mb: 2,
            }}
          >
            Are you sure you want to submit the proposal?
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              backgroundColor: "#F9FAFB",
            }}
          >
            {projectImage ? (
              <Box
                component="img"
                src={
                  typeof projectImage === "string"
                    ? projectImage
                    : URL.createObjectURL(projectImage)
                }
                alt={projectName || "Project"}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "8px",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "8px",
                  flexShrink: 0,
                  backgroundColor: "#E5E7EB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#9CA3AF",
                }}
              >
                <ImageOutlinedIcon />
              </Box>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#0D0D0E",
                  fontFamily: "Inter Tight",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {projectName || "Untitled"}
              </Typography>
              {proposalDescription && (
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "#6B7280",
                    fontFamily: "Inter Tight",
                    mt: 0.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {proposalDescription}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 , display: "flex", justifyContent: "space-between", gap: 1}}>
          <SecondaryButton onClick={closeSubmitConfirm} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={confirmSubmitProposal} disabled={saving}>
            {saving ? "Submitting..." : "Submit"}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RFCPricing;
